import * as React from 'react';
import { connect } from 'react-redux';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Spinner from 'components/spinner/Spinner';
import SidePanel from './SidePanel';
import SidePanelItem from './items/SidePanelItem';

import { downloadIndividualOffer } from 'modules/campaigns/reducers/campaignIndividualOffer';

import { getEntityFromProfile } from 'utils/view';
import { t } from 'utils/language';
import { insertIf } from 'utils/list';
import { canCampaignBeChanged } from 'utils/campaign';
import { canCancelSubcampaign, isAccepted, isPending } from 'utils/subcampaign';

import type { Campaign, Subcampaign } from 'types';

type OwnProps = {
  campaign: Campaign,
  subcampaign: Subcampaign,
  isMessengerDisabled: boolean,
  onClosePanel: () => void,
  onOpenPanel: (a: string) => void
};

type DispatchProps = {
  dispatchDownloadIndividualOffer: (a: number) => Promise<void>
};

type Props = OwnProps & DispatchProps;

export class SidePanelActions extends React.Component<Props> {
  handleDownloadOffer = () => {
    const { subcampaign, dispatchDownloadIndividualOffer } = this.props;

    dispatchDownloadIndividualOffer(subcampaign.id);
  };

  openPanel = (panelName: string) => {
    const { onOpenPanel } = this.props;

    onOpenPanel(panelName);
  };

  getSidePanelItems(campaign: Campaign, subcampaign: Subcampaign): Array<any> {
    const { isMessengerDisabled } = this.props;
    const entity = getEntityFromProfile(subcampaign.creatorProfile);
    const isPendingOrAcceptedSubcampaign = isAccepted(subcampaign) || isPending(subcampaign);

    const items = ([
      {
        title: t('messenger.side_panel.offer_label'),
        items: [
          {
            type: 'link',
            text: t('messenger.side_panel.view_offer_label'),
            link: `/campaigns/${String(campaign.id)}/subcampaigns/${subcampaign.id}`
          },
          ...insertIf(canCampaignBeChanged(campaign),
            {
              type: 'link',
              text: t('messenger.side_panel.change_offer_label'),
              link: `/campaigns/${String(campaign.id)}/subcampaigns/${subcampaign.id}/edit`
            }
          ),
          ...insertIf(isPendingOrAcceptedSubcampaign,
            {
              text: t('messenger.side_panel.download_offer_label'),
              meta: {
                onClick: this.handleDownloadOffer
              }
            }
          ),
          ...insertIf(canCancelSubcampaign(subcampaign),
            {
              type: 'cancel_offer',
              text: t('messenger.side_panel.cancel_offer_label'),
              meta: {
                subcampaign
              }
            }
          )
        ]
      },
      ...insertIf(!isMessengerDisabled,
        {
          title: t('messenger.side_panel.media_label'),
          items: [
            {
              type: 'generic',
              text: t('messenger.side_panel.attachments_label'),
              meta: {
                onClick: () => this.openPanel('attachments')
              }
            },
            {
              type: 'generic',
              text: t('messenger.side_panel.links_label'),
              meta: {
                onClick: () => this.openPanel('links')
              }
            }
          ]
        }
      ),
      {
        title: t('messenger.side_panel.creator_label'),
        items: [
          {
            type: 'lists',
            text: t('messenger.side_panel.lists_label'),
            meta: {
              subcampaignId: subcampaign.id,
              influencer: subcampaign.creatorProfile
            }
          },
          {
            type: 'link',
            text: t('messenger.side_panel.profile_label'),
            link: entity?.url
          },
          ...insertIf((subcampaign.shippingAddress && subcampaign.shippingAddress.address1), {
            type: 'shipping_address',
            text: t('messenger.side_panel.shipping_address_label'),
            meta: {
              subcampaign
            }
          }),
          {
            type: 'phone_number',
            text: t('messenger.side_panel.phone_number_label'),
            meta: {
              subcampaign
            }
          },
          {
            type: 'business_details',
            text: t('messenger.side_panel.business_details_label'),
            meta: {
              subcampaign
            }
          },
          {
            type: 'bank_details',
            text: t('messenger.side_panel.bank_details_label'),
            meta: {
              subcampaign
            }
          },
          {
            type: 'generic',
            name: 'notes',
            text: t('messenger.side_panel.notes_label'),
            meta: {
              influencer: subcampaign.creatorProfile,
              onClick: () => this.openPanel('notes')
            }
          }
        ]
      }
    ]);

    return items;
  }

  renderSidePanelContent = (campaign: Campaign, subcampaign: Subcampaign): Array<JSX.Element> =>
    this.getSidePanelItems(campaign, subcampaign)
      .map(section => (
        <div className="side-panel-actions__section" key={`side-panel-actions-${section.title}`}>
          <h4 className="side-panel-actions__title">{section.title}</h4>
          <ul className="side-panel-actions__items">
            {
              section.items.map((item, index) => (
                <li
                  className="side-panel-actions__item"
                  key={`side-panel-actions-${section.title}-${index}`}
                >
                  <SidePanelItem item={item} />
                </li>
              ))
            }
          </ul>
        </div>
      ));

  render(): React.ReactNode {
    const { campaign, subcampaign, onClosePanel } = this.props;

    return (
      <SidePanel>
        <section className="side-panel-actions">
          <div className="side-panel-actions__header">
            <span>{t('messenger.side_panel.actions_label')}</span>
            <div
              className="side-panel-actions__close-button"
              onClick={onClosePanel}
            >
              <SVGDynamicIcon
                name="cross-custom-fg"
                width={10}
                height={10}
                customColor="grey"
              />
            </div>
          </div>
          {
            (!campaign || !subcampaign) ? (
              <Spinner
                position="inline"
                size="small"
                color="light"
              />
            ) : (
              this.renderSidePanelContent(campaign, subcampaign)
            )
          }
        </section>
      </SidePanel>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDownloadIndividualOffer: (subcampaignId: number) =>
    dispatch(downloadIndividualOffer(subcampaignId, true))
});

export default connect(null, mapDispatchToProps)(SidePanelActions);
